import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Globe, Copy, Check, Share2, X, AlertCircle } from "lucide-react";
import API from "../api/api";
import "../style/share-modal.css";

export default function ShareModal({ isOpen, onClose, data }) {
  const [shareUrl, setShareUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showLearnMore, setShowLearnMore] = useState(false);
  const inputRef = useRef(null);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, onClose]);

  // Create share link when modal opens with data
  useEffect(() => {
    if (!isOpen || !data?.content) return;

    let isMounted = true;
    setLoading(true);
    setError(null);
    setCopied(false);
    setShowLearnMore(false);

    const createShareLink = async () => {
      try {
        const payload = {
          content: data.content,
          question: data.question || "",
          title: data.title || "GRGPT Response",
          sources: data.sources || [],
          metadata: data.metadata || {},
          chatId: data.chatId || null,
        };

        const res = await API.post("/chat/share", payload);
        if (isMounted && res.data?.success) {
          const generatedId = res.data.shareId;
          const fullUrl = `${window.location.origin}/share/${generatedId}`;
          setShareUrl(fullUrl);
        } else if (isMounted) {
          setError(res.data?.message || "Failed to generate public share link.");
        }
      } catch (err) {
        if (isMounted) {
          console.error("Share creation failed:", err);
          setError("Failed to create share link. Please try again.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    createShareLink();

    return () => {
      isMounted = false;
    };
  }, [isOpen, data]);

  if (!isOpen) return null;

  const copyToClipboard = async () => {
    if (!shareUrl) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else if (inputRef.current) {
        inputRef.current.select();
        document.execCommand("copy");
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error("Clipboard copy error:", err);
    }
  };

  const handleNativeShare = async () => {
    if (!shareUrl) return;
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: data?.title || "GRGPT Response",
          text: data?.question ? `Question: ${data.question}\n\nRead the GRGPT response:` : "Read this response on GRGPT:",
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          console.warn("Native share error:", err);
        }
      }
    } else {
      copyToClipboard();
    }
  };

  const encodedText = encodeURIComponent(
    data?.question
      ? `Check out this response on GRGPT: "${data.question.substring(0, 60)}..."\n${shareUrl}`
      : `Check out this response on GRGPT:\n${shareUrl}`
  );

  return ReactDOM.createPortal(
    <div className="share-modal-overlay" onClick={onClose}>
      <div
        className="share-modal-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
      >
        {/* Header */}
        <div className="share-modal-header">
          <div className="share-modal-title-row">
            <div className="share-modal-icon-badge">
              <Globe size={18} className="share-modal-globe-icon" />
            </div>
            <div className="share-modal-headings">
              <h3 id="share-modal-title" className="share-modal-title">
                Share a public link to this message
              </h3>
              <p className="share-modal-subtitle">
                Anyone with this link can access this message.{" "}
                <button
                  type="button"
                  className="share-modal-learn-more-btn"
                  onClick={() => setShowLearnMore((prev) => !prev)}
                >
                  {showLearnMore ? "Hide details" : "Learn more"}
                </button>
              </p>
            </div>
          </div>
          <button
            type="button"
            className="share-modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Expandable "Learn more" explanatory notice */}
        {showLearnMore && (
          <div className="share-modal-info-banner">
            <p>
              Only this specific question and response will be shared. Your other conversations, account details, and private history remain completely private. Anyone who receives this link can view the response even without a GRGPT account.
            </p>
          </div>
        )}

        {/* Message Preview */}
        <div className="share-modal-preview">
          {data?.question && (
            <div className="share-preview-question">
              <span className="share-preview-label">Prompt:</span>
              <p className="share-preview-question-text">{data.question}</p>
            </div>
          )}
          <div className="share-preview-response">
            <span className="share-preview-label">Response:</span>
            <p className="share-preview-response-snippet">
              {data?.content?.length > 220
                ? data.content.substring(0, 220).replace(/[#*`_]/g, "") + "..."
                : data?.content?.replace(/[#*`_]/g, "")}
            </p>
          </div>
        </div>

        {/* Share Link Generation Box */}
        <div className="share-modal-body">
          {loading ? (
            <div className="share-modal-loading">
              <div className="share-spinner" />
              <span>Generating public link...</span>
            </div>
          ) : error ? (
            <div className="share-modal-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          ) : (
            <>
              <div className="share-link-input-group">
                <input
                  ref={inputRef}
                  type="text"
                  readOnly
                  value={shareUrl}
                  onClick={(e) => e.target.select()}
                  className="share-link-input"
                  aria-label="Public share link"
                />
                <button
                  type="button"
                  onClick={copyToClipboard}
                  className={`share-copy-btn ${copied ? "copied" : ""}`}
                >
                  {copied ? (
                    <>
                      <Check size={15} />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy size={15} />
                      <span>Copy link</span>
                    </>
                  )}
                </button>
              </div>

              {/* Action Buttons: Native Share + Social Quick Links */}
              <div className="share-actions-row">
                {typeof navigator.share === "function" && (
                  <button
                    type="button"
                    onClick={handleNativeShare}
                    className="share-secondary-btn"
                  >
                    <Share2 size={14} />
                    <span>Share via device...</span>
                  </button>
                )}

                <a
                  href={`https://api.whatsapp.com/send?text=${encodedText}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-quick-pill"
                  title="Share on WhatsApp"
                >
                  WhatsApp
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
